import { useEffect, useState } from 'react';

const PERMISSIONS_STORAGE_KEY = 'permissions';

type PermissionCondition = 'AND' | 'OR';

/**
 * Hook to check if the current user has the required permission(s).
 *
 * @param requiredPermissions - Array of permission keys to check against.
 * @param condition - 'OR' (default) = any one permission is enough, 'AND' = all permissions required.
 * @returns boolean - true if the user satisfies the permission condition.
 *
 * @example
 * // Check if user has at least one of these permissions
 * const canView = useHasPermission(['view_users', 'manage_users']);
 *
 * // Check if user has ALL of these permissions
 * const canManage = useHasPermission(['create_users', 'delete_users'], 'AND');
 */
const useHasPermission = (
  requiredPermissions: string[],
  condition: PermissionCondition = 'OR',
): boolean => {
  const [hasPermission, setHasPermission] = useState<boolean>(false);

  useEffect(() => {
    const userPermissions: string[] = JSON.parse(
      sessionStorage.getItem(PERMISSIONS_STORAGE_KEY) || '[]',
    );

    let permissionFound = false;

    if (condition === 'AND') {
      permissionFound = requiredPermissions.every((permission) =>
        userPermissions.includes(permission),
      );
    } else {
      permissionFound = requiredPermissions.some((permission) =>
        userPermissions.includes(permission),
      );
    }

    setHasPermission(permissionFound);
  }, [requiredPermissions, condition]);

  return hasPermission;
};

export default useHasPermission;
